import React, { useEffect, useRef, useState } from 'react';
import { CloudRain, Pause, Play, Volume2, VolumeX, Waves, Wind } from 'lucide-react';

const sounds = [
  { id: 'ocean', label: 'Ocean', icon: Waves },
  { id: 'rain', label: 'Soft rain', icon: CloudRain },
  { id: 'warm', label: 'Warm noise', icon: Wind }
];

const createNoiseBuffer = (context, kind) => {
  const length = context.sampleRate * 5;
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  let previous = 0;
  for (let index = 0; index < length; index += 1) {
    const white = (Math.random() * 2) - 1;
    if (kind === 'warm') {
      previous = (previous + (0.02 * white)) / 1.02;
      data[index] = previous * 3.2;
    } else {
      data[index] = white;
    }
  }
  return buffer;
};

const createSound = (context, preset, volume) => {
  const source = context.createBufferSource();
  const filter = context.createBiquadFilter();
  const gain = context.createGain();
  source.buffer = createNoiseBuffer(context, preset);
  source.loop = true;

  if (preset === 'rain') {
    filter.type = 'highpass';
    filter.frequency.value = 900;
    gain.gain.value = volume * 0.28;
  } else {
    filter.type = 'lowpass';
    filter.frequency.value = preset === 'ocean' ? 620 : 480;
    gain.gain.value = volume * (preset === 'ocean' ? 0.42 : 0.34);
  }

  source.connect(filter).connect(gain).connect(context.destination);
  let tide;
  let tideDepth;
  if (preset === 'ocean') {
    tide = context.createOscillator();
    tideDepth = context.createGain();
    tide.frequency.value = 0.12;
    tideDepth.gain.value = volume * 0.18;
    tide.connect(tideDepth).connect(gain.gain);
    tide.start();
  }
  source.start();
  return { source, gain, tide };
};

export default function AmbientPlayer() {
  const engineRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [preset, setPreset] = useState('ocean');
  const [volume, setVolume] = useState(0.5);

  const stop = () => {
    const engine = engineRef.current;
    if (!engine) return;
    try { engine.source.stop(); } catch (_error) { /* already stopped */ }
    try { engine.tide?.stop(); } catch (_error) { /* already stopped */ }
    engineRef.current = null;
  };

  const start = async (nextPreset = preset) => {
    stop();
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const context = window.__verseDeliveryAudioContext || new AudioContext();
    window.__verseDeliveryAudioContext = context;
    await context.resume();
    engineRef.current = createSound(context, nextPreset, volume);
    setPlaying(true);
  };

  const toggle = () => {
    if (playing) {
      stop();
      setPlaying(false);
    } else {
      start();
    }
  };

  const selectPreset = (nextPreset) => {
    setPreset(nextPreset);
    if (playing) start(nextPreset);
  };

  const changeVolume = (event) => {
    const next = Number(event.target.value);
    setVolume(next);
    if (engineRef.current) engineRef.current.gain.gain.value = next * 0.34;
  };

  useEffect(() => () => stop(), []);

  const activeSound = sounds.find((sound) => sound.id === preset);
  return (
    <aside className={`vd-lofi-player ${playing ? 'is-playing' : ''}`} aria-label="Ambient sound player">
      <button className="vd-ambient-play" onClick={toggle} aria-label={playing ? 'Pause ambient sound' : 'Play ambient sound'}>
        {playing ? <Pause size={17} /> : <Play size={17} />}
      </button>
      <div className="vd-ambient-now">
        <small>{playing ? 'Playing softly' : 'Quiet sounds'}</small>
        <strong>{activeSound.label}</strong>
      </div>
      <div className="vd-ambient-sounds" aria-label="Choose an ambient sound">
        {sounds.map(({ id, label, icon: Icon }) => (
          <button key={id} className={preset === id ? 'is-active' : ''} onClick={() => selectPreset(id)} aria-label={label} title={label}>
            <Icon size={15} /><span>{label}</span>
          </button>
        ))}
      </div>
      <div className="vd-ambient-volume">
        {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
        <input aria-label="Ambient sound volume" type="range" min="0" max="1" step=".05" value={volume} onChange={changeVolume} />
      </div>
    </aside>
  );
}
